import { AccountEffectsModule } from '@account/effects';
import { AccountEntityModule } from '@account/entity';
import { provideAnalytics } from '@analytics/core';
import { AdobeLaunchService, GddlInspectorService } from '@analytics/datalayer';
import {
  ABTestEnhancer,
  NotFoundAnalyticsEnhancer,
} from '@analytics/enhancers';
import {
  AnalyticsDirectivesModule,
  PageVisitDurationSideEffect,
  QualityVisitSideEffect,
  SitecoreABTestSideEffect,
} from '@analytics/events';
import { provideJourneyAnalytics } from '@analytics/journey';
import {
  APP_BASE_HREF,
  CommonModule,
  registerLocaleData,
} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeNL from '@angular/common/locales/nl';
import type { ModuleWithProviders } from '@angular/core';
import { LOCALE_ID, NgModule, Optional, SkipSelf } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { FocusResetOnNavigationComponent } from '@core/accessibility';
import { ConfigModule, CONFIGMODULE_CONFIG } from '@core/config-angular';
import { CoreConfigStoreModule } from '@core/config-store';
import { coreNavigationLazyImports } from '@core/core-navigation-lazy';
import { CoreExperienceEditorUtilsModule } from '@core/experience-editor-utils';
import { CoreJssRoutingModule } from '@core/jss-routing';
import { CoreJssSeoModule } from '@core/jss-seo';
import { CoreNavigationStoreModule } from '@core/navigation-store';
import { FooterContainerComponent } from '@core/navigation-ui/footer';
import { CorePlatformModule } from '@core/platform';
import { CoreRoutingModule } from '@core/routing';
import { SettingsModule } from '@core/settings';
import { CoreTranslateModule } from '@core/translate';
import { CustomerEntityModule } from '@customer/entity';
import { CommonCoreModule, RequestStrategy } from '@essent/common';
import { FunctionalErrorsModule } from '@functional-errors/ui';
import { ABTestingLazyImports } from '@innogy/ab-testing/lazy';
import { LoadAccountComponent } from '@innogy/account/components';
import {
  ChatbotIadvizeComponent,
  ChatbotIadvizeStartScreenComponent,
} from '@innogy/chatbot/iadvize';
import { commonUiLazyImports } from '@innogy/common-ui/lazy';
import { CookiewallContainerComponent } from '@innogy/cookie-settings';
import { InitializeModule } from '@innogy/core/initialize';
import { CoreLoadFontsModule } from '@innogy/core/load-fonts';
import { DebugModule, ErrorComponent } from '@innogy/debug';
import { MemberGetMemberModule } from '@innogy/shared/member-get-member';
import { SharedResponsiveToolsModule } from '@innogy/shared/responsive-tools';
import { KnowledgeBaseAnalyticsEnhancer } from '@innogy/knowledge-base/shared';
import {
  extModules,
  metaReducers,
  reducers,
  runtimeChecks,
} from '@innogy/utils-state';
import { EffectsModule } from '@ngrx/effects';
import {
  FullRouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { WINDOW_PROVIDERS } from '@innogy/utils-dom';
import { ConversationalChatUiComponent } from '@conversational/chat-ui';

import { TransferHttpCacheInterceptor } from './transfer-http-cache-interceptor';
import { ConversationalChatStoreModule } from '@conversational/chat-store';

registerLocaleData(localeNL, 'nl');

export interface CoreModulesConfig {
  appName: string;
  basePath: string;
}

@NgModule({
  imports: [
    CorePlatformModule,
    CommonCoreModule.forRoot({
      defaultRequestStrategy: RequestStrategy.IGNORE,
    }),
    BrowserModule.withServerTransition({ appId: 'innogy-app' }),
    EffectsModule.forRoot([]),
    LazyLoadImageModule,
    SharedResponsiveToolsModule.forRoot(),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks,
    }),
    CoreConfigStoreModule,
    StoreRouterConnectingModule.forRoot({
      serializer: FullRouterStateSerializer,
      stateKey: 'router',
    }),
    AnalyticsDirectivesModule,
    CoreExperienceEditorUtilsModule,
    CoreTranslateModule.forRoot(),
    CoreNavigationStoreModule,
    ChatbotIadvizeComponent,
    ChatbotIadvizeStartScreenComponent,
    ConversationalChatUiComponent,
    ConversationalChatStoreModule,
    MemberGetMemberModule,
    CookiewallContainerComponent,
    FocusResetOnNavigationComponent,
    LoadAccountComponent,
    ...extModules,
  ],
  exports: [
    LoadAccountComponent,
    AccountEntityModule,
    AccountEffectsModule,
    BrowserModule,
    CommonModule,
    ConfigModule,
    CoreLoadFontsModule,
    CoreRoutingModule,
    CustomerEntityModule,
    DebugModule,
    InitializeModule,
    CoreJssRoutingModule,
    CoreTranslateModule,
    CoreJssSeoModule,
    ReactiveFormsModule,
    FunctionalErrorsModule,
    SettingsModule,
    ChatbotIadvizeComponent,
    ChatbotIadvizeStartScreenComponent,
    ConversationalChatUiComponent,
    MemberGetMemberModule,
    CookiewallContainerComponent,
    FocusResetOnNavigationComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'nl' },
    { provide: APP_BASE_HREF, useValue: '' },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: TransferHttpCacheInterceptor,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideJourneyAnalytics(),
    provideAnalytics({
      enhancers: [
        NotFoundAnalyticsEnhancer,
        ABTestEnhancer,
        KnowledgeBaseAnalyticsEnhancer,
      ],
      sideEffects: [
        PageVisitDurationSideEffect,
        SitecoreABTestSideEffect,
        QualityVisitSideEffect,
      ],
      outputs: [GddlInspectorService, AdobeLaunchService],
    }),
  ],
})
export class CoreModulesCommonModule {
  public constructor(
    @Optional()
    @SkipSelf()
    parentModule: CoreModulesCommonModule
  ) {
    if (parentModule) {
      throw new Error('CoreModulesCommonModule is imported more than once.');
    }
  }

  static forRoot(
    config: CoreModulesConfig
  ): ModuleWithProviders<CoreModulesCommonModule> {
    return {
      ngModule: CoreModulesCommonModule,
      providers: [
        {
          provide: CONFIGMODULE_CONFIG,
          useValue: {
            appName: config.appName,
            basePath: config.basePath,
          } as CoreModulesConfig,
        },
        WINDOW_PROVIDERS,
      ],
    };
  }
}

export const COMMON_JSS_IMPORTS = [
  { name: 'DebugError', type: ErrorComponent },
  { name: 'Footer', type: FooterContainerComponent },
];

export const COMMON_JSS_LAZY_IMPORTS = [
  ...commonUiLazyImports,
  ...ABTestingLazyImports,
  ...coreNavigationLazyImports,
];
